import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminClient } from '@/lib/adminClient';
import type { NimCallInitiator } from '@/api/generated';
import type { PlaceNimCallRequest } from '@/api/types';
import { isNimCallsUnavailable, nimCallsPollInterval } from '@/features/nim/components/nimCalls';

export const nimCallKeys = {
  all: ['nim-calls'] as const,
  lists: () => [...nimCallKeys.all, 'list'] as const,
  list: (initiator: NimCallInitiator | undefined, pageSize: number, pageToken: string) =>
    [...nimCallKeys.lists(), initiator ?? '', pageSize, pageToken] as const,
  users: (userIds: readonly string[]) => [...nimCallKeys.all, 'users', userIds] as const,
};

/** A page of calls, newest first, polled while one rings or is live. */
export function useNimCalls(params: {
  initiator: NimCallInitiator | undefined;
  pageSize: number;
  pageToken: string;
  enabled: boolean;
}) {
  return useQuery({
    queryKey: nimCallKeys.list(params.initiator, params.pageSize, params.pageToken),
    queryFn: () =>
      adminClient.listNimCalls({
        initiator: params.initiator,
        pageSize: params.pageSize,
        pageToken: params.pageToken || undefined,
      }),
    enabled: params.enabled,
    refetchInterval: (query) => nimCallsPollInterval(query.state.data?.calls ?? []),
    // Unavailable is this environment having no voice service, not a blip
    // (the gateway has already retried it): retrying again only delays the
    // tab saying so.
    retry: (failureCount, error) => !isNimCallsUnavailable(error) && failureCount < 1,
  });
}

/**
 * The profiles of the users on a page of calls, keyed by user id, in one
 * batched request (LIM-1832). The calls carry only the id; an unknown id is
 * left out, and its row shows the id.
 */
export function useNimCallUsers(userIds: readonly string[]) {
  const unique = Array.from(new Set(userIds)).sort();
  return useQuery({
    queryKey: nimCallKeys.users(unique),
    queryFn: () => adminClient.adminBatchGetUsers(unique),
    enabled: unique.length > 0,
    staleTime: 10 * 60 * 1000,
  });
}

export function usePlaceNimCall() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: PlaceNimCallRequest) => adminClient.placeNimCall(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: nimCallKeys.lists() });
    },
  });
}
